import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, ArrowRight, Camera, Truck, Image as ImageIcon } from "lucide-react";
import { Reveal, SectionHeading, SmartImage } from "../components/common";
import EnquiryForm from "../components/EnquiryForm";
import { gallery, canvasPrice, photographyHero, business } from "../data/mock";

export default function Photography() {
  const [active, setActive] = useState(null);

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-24">
        <div className="absolute inset-0 z-0">
          <SmartImage src={photographyHero} alt="Landscape" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/60 to-slate-900/40" />
        </div>
        <div className="max-w-7xl mx-auto px-5 relative z-10">
          <Reveal>
            <span className="inline-block text-sm font-semibold tracking-wider uppercase text-sky-300 mb-3">Fred's Photography</span>
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white max-w-2xl leading-tight">Prints that bring the outdoors indoors.</h1>
            <p className="mt-5 text-lg text-slate-200 max-w-xl">Every photo is Fred's own — shot around Cobram, the Murray, and beyond. Available as canvas prints, delivered to your door.</p>
            <div className="mt-8 inline-flex items-center gap-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-6 py-4">
              <div><div className="text-xs text-slate-300 uppercase tracking-wide">Canvas prints from</div><div className="font-heading text-3xl font-bold text-white">${canvasPrice}</div></div>
              <div className="h-10 w-px bg-white/20" />
              <p className="text-sm text-slate-200 max-w-[16rem]">Custom sizes available on request — email {business.email}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5">
          <SectionHeading eyebrow="The Gallery" title="A window to the country you know." subtitle="Tap any photo to view it larger. Note the ones you like and email Fred your favourites." />
          <div className="mt-12 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
            {gallery.map((g, i) => (
              <Reveal key={g.title} delay={(i % 3) * 0.05} className="mb-5 break-inside-avoid">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setActive(g)}
                  className="group relative block w-full overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-shadow"
                >
                  <SmartImage src={g.src} fallback={g.fb} alt={g.title} className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 inset-x-0 p-4 flex items-end justify-between translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                    <span className="font-heading font-semibold text-white">{g.title}</span>
                    {g.price && <span className="rounded-full bg-sky-500 text-white text-xs font-semibold px-2.5 py-1">Canvas · ${canvasPrice}</span>}
                  </div>
                </motion.button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5">
          <SectionHeading eyebrow="How it works" title="Pick a photo. Fred prints it. Delivered to your door." />
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { icon: ImageIcon, title: "Browse the gallery", desc: "Have a look through and note the ones you like." },
              { icon: Mail, title: "Email your choice", desc: "Send Fred your favourites and preferred canvas size." },
              { icon: Truck, title: "Delivered to your door", desc: "Your custom canvas is printed and shipped to your front door." },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="relative h-full rounded-2xl bg-white border border-slate-200 p-7 shadow-sm">
                  <span className="absolute -top-4 left-7 flex items-center justify-center w-9 h-9 rounded-full bg-sky-500 text-white font-heading font-bold shadow-lg shadow-sky-500/30">{i + 1}</span>
                  <s.icon className="text-sky-500 mt-3" size={28} />
                  <h3 className="mt-4 font-heading font-bold text-lg text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENQUIRY */}
      <section className="py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-5">
          <SectionHeading eyebrow="Order a Canvas" title="Enquire about a print." subtitle="Tell Fred which photo(s) and the size you'd like — he'll come back with a quote." />
          <div className="mt-10">
            <EnquiryForm
              type="canvas"
              cta="Send Canvas Enquiry"
              extraFields={[
                { name: "photo", label: "Which photo(s)?", placeholder: "e.g. Sunrise, Water Reflections" },
                { name: "size", label: "Preferred canvas size", placeholder: "e.g. 60 x 90 cm" },
              ]}
            />
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[60] bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-5"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
          >
            <button className="absolute top-5 right-5 text-white/80 hover:text-white" onClick={() => setActive(null)}><X size={32} /></button>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}
            >
              <SmartImage src={active.src} fallback={active.fb} alt={active.title} className="w-full max-h-[75vh] object-contain rounded-xl" />
              <div className="mt-4 flex items-center justify-between">
                <div><h3 className="font-heading font-bold text-white text-xl">{active.title}</h3><p className="text-slate-300 text-sm">Available as a canvas print from ${canvasPrice}</p></div>
                <a href={`${business.emailHref}?subject=Canvas%20Print%20Enquiry%20-%20${encodeURIComponent(active.title)}`} className="inline-flex items-center gap-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold px-5 py-2.5 transition-colors">Enquire <ArrowRight size={16} /></a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
